import {
  Alert,
  Button,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Vorgangstyp } from "@msc/shared";

import { useCurrentUser } from "../hooks/useAuth.js";
import {
  useDailyTeams,
  useTeamDailyBoard,
  useTeamDailyKennzahlen,
  useTeamDailyNews,
  useTeamDailyPresence,
  useTeamDailySegments,
  useTeamDailyTeam,
} from "../hooks/useTeamDaily.js";
import { startTeam, teamLaden, teamSpeichern } from "../lib/teamGedaechtnis.js";
import { BoardColumns } from "../components/teamDaily/BoardColumns.js";
import { BudgetTable } from "../components/teamDaily/BudgetTable.js";
import { IntakePanel } from "../components/teamDaily/IntakePanel.js";
import { KennzahlenLeiste } from "../components/teamDaily/KennzahlenLeiste.js";
import { NewsTicker } from "../components/teamDaily/NewsTicker.js";
import { NotesPanel } from "../components/teamDaily/NotesPanel.js";
import { PresenceBar } from "../components/teamDaily/PresenceBar.js";
import { TeamVerwaltung } from "../components/teamDaily/TeamVerwaltung.js";
import { VerlaufDiagramm } from "../components/teamDaily/VerlaufDiagramm.js";

// ── Search-Param Validation ─────────────────────────────────────────────────

export interface TeamDailySearch {
  segmentId?: string;
  date?: string;
}

/**
 * Bereich und Tag stehen in der Adresse, damit ein Board **teilbar** ist: „mein
 * Bereich, heute" als Lesezeichen, ein bestimmter Tag als Link im Chat. Ohne
 * das musste beides nach jedem Aufruf neu eingestellt werden.
 *
 * Geprüft wird streng — was nicht wie eine Kennung oder ein Datum aussieht,
 * fällt weg und die Vorbelegung greift. Eine Adresszeile ist Nutzereingabe.
 */
export function validateTeamDailySearch(
  raw: Record<string, unknown>,
): TeamDailySearch {
  const segmentId = typeof raw.segmentId === "string" ? raw.segmentId : undefined;
  const date = typeof raw.date === "string" ? raw.date : undefined;
  return {
    segmentId:
      segmentId && /^[0-9a-f-]{36}$/i.test(segmentId) ? segmentId : undefined,
    date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : undefined,
  };
}

/** Heutiges Datum als `YYYY-MM-DD` in Europe/Berlin (nicht UTC). */
function todayInBerlin(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * TeamDaily — das tagesaktuelle Board eines Bereichs. Read-only (v1): Tickets je
 * Bearbeiter, Anwesenheit, „heute gebucht", Restbudgets, News-Ticker und
 * Tagesnotizen. Alle Rollen dürfen sehen; die eigentliche Reichweitengrenze
 * zieht der Server.
 */
export function TeamDailyPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const { data: segments, isLoading: loadingSegments } = useTeamDailySegments();

  const search = useSearch({ strict: false }) as TeamDailySearch;
  const navigate = useNavigate();

  const segmentId = search.segmentId ?? null;
  const date = search.date ?? todayInBerlin();
  // Die Mitgliederauswahl bleibt außerhalb der Adresse, wird aber **gemerkt**
  // (s. `lib/teamGedaechtnis.ts`). Sie stand zuerst nur im Seitenzustand und
  // war nach jedem Aktualisieren weg — wer sein Daily-Team einstellt, stellt
  // dasselbe morgen wieder ein. In die Adresse gehört sie trotzdem nicht:
  // Ein Link zu einem bestimmten Tag soll den Bereich zeigen, nicht das Team
  // dessen, der ihn verschickt hat.
  const [teamId, setTeamId] = useState<string | null>(null);
  const [verwaltungOffen, setVerwaltungOffen] = useState(false);
  // Steht die gemerkte Auswahl für diesen Bereich schon fest? Bis dahin
  // fragt das Board nicht — sonst lüde jeder Aufruf zuerst den ganzen Bereich
  // und gleich danach das Team, mit zwei Live-Abfragen an Jira für eine Ansicht.
  const [teamBereit, setTeamBereit] = useState(false);
  const [vorgangstyp, setVorgangstyp] = useState<Vorgangstyp | null>(null);

  const teamWaehlen = (id: string | null) => {
    setTeamId(id);
    if (segmentId) teamSpeichern(segmentId, id);
  };

  const setSegmentId = (id: string | null) => {
    void navigate({ to: "/teamdaily", search: { segmentId: id ?? undefined, date } });
  };
  const setDate = (d: string) => {
    void navigate({
      to: "/teamdaily",
      search: { segmentId: segmentId ?? undefined, date: d },
    });
  };

  // Bereich vorbelegen, sobald die erreichbaren Bereiche da sind — und in die
  // Adresse schreiben, damit ein Neuladen dieselbe Ansicht zeigt.
  useEffect(() => {
    if (!segmentId && segments && segments.length > 0) {
      const preferred =
        segments.find((s) => s.id === user?.defaultSegmentId) ?? segments[0]!;
      void navigate({
        to: "/teamdaily",
        search: { segmentId: preferred.id, date },
        replace: true,
      });
    }
  }, [segments, segmentId, user?.defaultSegmentId, date, navigate]);

  // Beim Bereichswechsel gilt die Auswahl des neuen Bereichs — die alte wäre
  // dort ohnehin nicht erreichbar (der Server prüft die Auswahl gegen den
  // Bereich).
  useEffect(() => {
    setTeamId(null);
    setTeamBereit(false);
  }, [segmentId]);

  const team = useTeamDailyTeam(segmentId);
  const teams = useDailyTeams(segmentId);

  // Gemerktes Team anwenden, sobald die Teams des Bereichs bekannt sind. Ein
  // gelöschtes Team fällt dabei weg (sonst antwortete das Board dauerhaft mit
  // `TEAM_NOT_FOUND`), und wer in genau einem Team steht, bekommt es ohne
  // Zutun — wer in mehreren steht, wird nicht geraten.
  useEffect(() => {
    if (teamBereit || !segmentId || !teams.data) return;
    const vorhanden = teams.data.teams.map((t) => t.id);
    const gewaehlt = startTeam(teamLaden(segmentId), vorhanden, teams.data.mine);
    setTeamId(gewaehlt);
    teamSpeichern(segmentId, gewaehlt);
    setTeamBereit(true);
  }, [teams.data, segmentId, teamBereit]);

  const board = useTeamDailyBoard(segmentId, date, teamId, teamBereit);
  const presence = useTeamDailyPresence(segmentId, date, teamId, teamBereit);
  const news = useTeamDailyNews(segmentId, teamId, teamBereit);
  const kennzahlen = useTeamDailyKennzahlen(segmentId, teamId, vorgangstyp, teamBereit);

  const teamOptions = useMemo(
    () => (teams.data?.teams ?? []).map((t) => ({ value: t.id, label: t.name })),
    [teams.data],
  );

  if (loadingSegments) {
    return <Skeleton height={320} />;
  }

  return (
    <Stack gap="md">
      <Title order={2}>{t("pages.teamDaily.title")}</Title>

      <Group align="flex-end" gap="md">
        {segments && segments.length > 1 ? (
          <Select
            label={t("common.segment")}
            value={segmentId}
            data={segments.map((s) => ({ value: s.id, label: s.name }))}
            onChange={setSegmentId}
            allowDeselect={false}
            style={{ minWidth: 220 }}
          />
        ) : (
          <div>
            <Text size="sm" fw={500}>
              {t("common.segment")}
            </Text>
            <Text>
              {segments?.find((s) => s.id === segmentId)?.name ?? ""}
            </Text>
          </div>
        )}

        <TextInput
          type="date"
          label={t("pages.teamDaily.date")}
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
        />

        <Select
          label={t("pages.teamDaily.teamPicker")}
          placeholder={t("pages.teamDaily.teamFilterAll")}
          data={teamOptions}
          value={teamId}
          onChange={teamWaehlen}
          searchable
          clearable
          style={{ minWidth: 240, flex: 1 }}
        />

        <Button variant="default" onClick={() => setVerwaltungOffen(true)}>
          {t("pages.teamDaily.teamsManage")}
        </Button>
      </Group>

      {presence.data && (
        <PresenceBar presence={presence.data} boardDate={date} />
      )}

      {/* Zähler und Trend aus dem abgelösten All-KPI-Dashboard: über dem
          Eingang, weil sie die Frage beantworten, mit der der Tag beginnt —
          was liegt, und läuft der Berg voll? */}
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <KennzahlenLeiste
          daten={kennzahlen.data}
          fehler={kennzahlen.isError}
          vorgangstyp={vorgangstyp}
          onVorgangstyp={setVorgangstyp}
        />
        <VerlaufDiagramm
          segmentId={segmentId}
          teamId={teamId}
          vorgangstyp={vorgangstyp}
          bereit={teamBereit}
        />
      </SimpleGrid>

      {/* Eingang, Notizen und News in einer Reihe — alles drei gehört vor die
          Personenkarten: Das eine will verteilt, das zweite im Gespräch
          mitgeschrieben, das dritte in einem Satz erwähnt werden. Die Notizen
          standen zuerst ganz unten, wo im Daily niemand mehr hinsieht. */}
      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="md">
        {board.data && (
          <IntakePanel
            tickets={board.data.intake}
            jiraBaseUrl={board.data.jiraBaseUrl}
          />
        )}
        <NotesPanel segmentId={segmentId} teamId={teamId} date={date} />
        {news.data && <NewsTicker items={news.data} />}
      </SimpleGrid>

      {board.isLoading && <Skeleton height={320} />}

      {board.isError && (
        <Alert color="red" variant="light" title={t("common.error")}>
          {(board.error as Error | null)?.message ?? t("common.errorLoading")}
        </Alert>
      )}

      {board.data && (
        <>
          {!board.data.jiraLive && (
            <Alert
              color="yellow"
              variant="light"
              title={t("pages.teamDaily.jiraOfflineTitle")}
            >
              {board.data.jiraError ?? t("pages.teamDaily.jiraOfflineBody")}
            </Alert>
          )}

          {board.data.members.length === 0 ? (
            <Alert color="gray" variant="light">
              {t("pages.teamDaily.noMembers")}
            </Alert>
          ) : (
            <BoardColumns board={board.data} />
          )}
        </>
      )}

      {board.data && board.data.budgets.length > 0 && (
        <BudgetTable budgets={board.data.budgets} />
      )}

      {segmentId && (
        <TeamVerwaltung
          opened={verwaltungOffen}
          onClose={() => setVerwaltungOffen(false)}
          segmentId={segmentId}
          teams={teams.data?.teams ?? []}
          personen={team.data?.members ?? []}
          aktivesTeam={teamId}
          onTeamGewaehlt={teamWaehlen}
        />
      )}
    </Stack>
  );
}
